import React, { useState, useContext, useEffect } from 'react';
import { AuthContext } from '../context/AuthContext';
import { useSidebar } from '../context/SidebarContext';

import SidePanel from '../components/Common/SidePanel';
import MobileMenuToggle from '../components/Common/MobileMenuToggle';
import ResponsiveTable from '../components/Common/ResponsiveTable';
import { Search, Eye, CheckCircle, XCircle, Loader, AlertCircle } from 'lucide-react';
import { barcodeService } from '../services/api';

const Barcodes = () => {
  const { user } = useContext(AuthContext);
  const { isCollapsed } = useSidebar();
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [barcodes, setBarcodes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [page, setPage] = useState(1);
  const limit = 50;

  useEffect(() => {
    fetchBarcodes();
  }, [searchTerm, statusFilter, page]);

  const fetchBarcodes = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await barcodeService.getAll(
        statusFilter || null,
        limit,
        page,
        searchTerm || null
      );
      setBarcodes(response.data?.data?.barcodes || []);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to load barcodes');
    } finally {
      setLoading(false);
    }
  };

  const handleActivate = async (id) => {
    try {
      await barcodeService.activate(id);
      fetchBarcodes();
    } catch (err) {
      alert(err.response?.data?.error || 'Failed to activate barcode');
    }
  };

  const handleDeactivate = async (id) => {
    try {
      await barcodeService.deactivate(id);
      fetchBarcodes();
    } catch (err) {
      alert(err.response?.data?.error || 'Failed to deactivate barcode');
    }
  };

  let filteredBarcodes = barcodes;

  if (loading) {
    return (
      <div className="flex h-screen bg-slate-50">
        <MobileMenuToggle onClick={() => setIsSidebarOpen(true)} isSidebarOpen={isSidebarOpen} />
        <SidePanel isOpen={isSidebarOpen} onClose={() => setIsSidebarOpen(false)} />
        <div className="flex-1 flex flex-col">
          <div className="flex-1 flex items-center justify-center">
            <Loader className="w-8 h-8 text-blue-600 animate-spin" />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex h-screen bg-slate-50">
      <MobileMenuToggle onClick={() => setIsSidebarOpen(true)} isSidebarOpen={isSidebarOpen} />
      <SidePanel isOpen={isSidebarOpen} onClose={() => setIsSidebarOpen(false)} />
      <div className="flex-1 flex flex-col overflow-hidden transition-all duration-300">
        <main className="flex-1 overflow-y-auto pt-28 md:pt-0">
          <div className="p-6 md:p-8 max-w-7xl mx-auto">
            <div className="mb-8">
              <h1 className="text-3xl font-bold text-slate-900 mb-2">Barcode Management</h1>
              <p className="text-slate-600">View and manage all barcodes</p>
            </div>

            {error && (
              <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg flex gap-3">
                <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
                <p className="text-red-800">{error}</p>
              </div>
            )}

            <div className="bg-white rounded-lg shadow">
              <div className="p-6 border-b border-slate-200 flex flex-col md:flex-row gap-4">
                <div className="relative flex-1 max-w-md">
                  <Search className="absolute left-3 top-3 w-5 h-5 text-slate-400" />
                  <input
                    type="text"
                    placeholder="Search barcodes..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="w-full pl-10 pr-4 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                <select
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                  className="px-4 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="">All Status</option>
                  <option value="NOT_ACTIVATED">Not Activated</option>
                  <option value="ACTIVATED">Activated</option>
                  <option value="DEACTIVATED">Deactivated</option>
                </select>
              </div>

              <ResponsiveTable
                columns={[
                  { key: 'barcodeNumber', label: 'Barcode Number' },
                  { key: 'status', label: 'Status' },
                  { key: 'createdAt', label: 'Created' },
                  { key: 'actions', label: 'Actions' }
                ]}
                data={filteredBarcodes}
                renderRow={(b, idx, isMobileCard) => (
                  isMobileCard ? (
                    <div key={b.id} className="space-y-3">
                      <div className="flex justify-between items-start">
                        <div>
                          <p className="text-xs text-slate-500 font-semibold uppercase">Barcode Number</p>
                          <p className="text-sm font-semibold text-slate-900">{b.barcodeNumber}</p>
                        </div>
                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                          b.status === 'ACTIVATED' ? 'bg-green-100 text-green-800' :
                          b.status === 'NOT_ACTIVATED' ? 'bg-yellow-100 text-yellow-800' :
                          'bg-red-100 text-red-800'
                        }`}>
                          {b.status?.replace('_', ' ')}
                        </span>
                      </div>
                      <div>
                        <p className="text-xs text-slate-500 font-semibold uppercase">Created</p>
                        <p className="text-sm text-slate-700">{new Date(b.createdAt).toLocaleDateString()}</p>
                      </div>
                      <div className="flex gap-2 pt-2 border-t border-slate-100">
                        {b.status === 'NOT_ACTIVATED' && (
                          <button
                            onClick={() => handleActivate(b.id)}
                            className="flex-1 px-3 py-2 bg-green-50 hover:bg-green-100 text-green-700 rounded-lg transition text-sm font-medium flex items-center justify-center gap-2"
                            title="Activate"
                          >
                            <CheckCircle className="w-4 h-4" />
                            Activate
                          </button>
                        )}
                        {b.status === 'ACTIVATED' && (
                          <button
                            onClick={() => handleDeactivate(b.id)}
                            className="flex-1 px-3 py-2 bg-red-50 hover:bg-red-100 text-red-700 rounded-lg transition text-sm font-medium flex items-center justify-center gap-2"
                            title="Deactivate"
                          >
                            <XCircle className="w-4 h-4" />
                            Deactivate
                          </button>
                        )}
                      </div>
                    </div>
                  ) : (
                    <tr key={b.id} className="border-b border-slate-200 hover:bg-slate-50">
                      <td className="px-6 py-4 font-medium">{b.barcodeNumber}</td>
                      <td className="px-6 py-4">
                        <span className={`px-3 py-1 rounded-full text-sm ${
                          b.status === 'ACTIVATED' ? 'bg-green-100 text-green-800' :
                          b.status === 'NOT_ACTIVATED' ? 'bg-yellow-100 text-yellow-800' :
                          'bg-red-100 text-red-800'
                        }`}>
                          {b.status?.replace('_', ' ')}
                        </span>
                      </td>
                      <td className="px-6 py-4 text-slate-600">{new Date(b.createdAt).toLocaleDateString()}</td>
                      <td className="px-6 py-4 text-right">
                        <div className="flex justify-end gap-2">
                          {b.status === 'NOT_ACTIVATED' && (
                            <button
                              onClick={() => handleActivate(b.id)}
                              className="p-2 hover:bg-green-50 rounded-lg transition"
                              title="Activate"
                            >
                              <CheckCircle className="w-4 h-4 text-green-600" />
                            </button>
                          )}
                          {b.status === 'ACTIVATED' && (
                            <button
                              onClick={() => handleDeactivate(b.id)}
                              className="p-2 hover:bg-red-50 rounded-lg transition"
                              title="Deactivate"
                            >
                              <XCircle className="w-4 h-4 text-red-600" />
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  )
                )}
              />
            </div>
          </div>
        </main>
      </div>
    </div>
  );
};

export default Barcodes;
